import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { DayView } from "./day-view";
import { MonthView } from "./month-view";
import * as analytics from "utils/analytics";
import { Page } from "components/page";
import { Flex, Heading, useDisclosure } from "@chakra-ui/react";
import { variant } from "theme/names";
import { ConfirmPopup } from "components/popups/confirmPopup";
import { rebuildQueue } from "controllers/schedule";
import { useSession } from "next-auth/react";
import { useTweetContext } from "context/tweetContext";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { ActionsDropdown } from "./actions-dropdown";
import { PostTypesDropdown } from "./post-types-dropdown";
import { ViewSelectorDropdown } from "./view-selector-dropdown";
import { format } from "date-fns";
const DEFAULT_TYPE = "schedule";

export default function Queue({ query }: { query: any }) {
  const { view = "day" } = query;
  const router = useRouter();
  const [selectedType, setSelectedType] = useState<string>(query.type ? query.type : DEFAULT_TYPE);
  const { data: session } = useSession() ?? {}
  const tweetContext: any = useTweetContext();
  const isAuthenticated = useFirebaseUser();
  const handleTypeChange = (newType: string) => {
    setSelectedType(newType);
    router.push({
      pathname: "queue",
      query: { type: newType, view },
    });
    analytics.log("queue_viewed", {
      type: newType,
      view: view,
    });
  };

  const sharedProps = {
    query,
    selectedType,
    onTypeChange: handleTypeChange,
  };
  useEffect(() => {
    if (isAuthenticated && query.text) {
      tweetContext.newTweet({ text: query.text });
      tweetContext.setIsTweetTextChanged(true);
      tweetContext.open();
    }
  }, [isAuthenticated, query.text]);
  const handleTabChange = (newType: string) => {
    setSelectedType(newType);
    router.push({
      pathname: "queue",
      query: { type: newType, view },
    });
  }
  const { isOpen: isOpenRebuild, onOpen: onOpenRebuild, onClose: onCloseRebuild } = useDisclosure();
  const { isOpen: isOpenShuffle, onOpen: onOpenShuffle, onClose: onCloseShuffle } = useDisclosure();
  return (
    <Page mainWrapperProps={{ px: 16, py: 8, maxW: 'initial', minH: '100vh' }}>
      <Flex alignItems="center" justifyContent="space-between">
        <Heading variant={variant.Heading.pageTitle} as="h1">
          My Queue  {view === "day" ? "" : `- ${format(new Date(), 'MMMM')}`}
        </Heading>
        <Flex alignItems="center" gap={4}>
          {view === "day" && <PostTypesDropdown
            selectedType={selectedType}
            onTypeChange={handleTabChange}
          />}
          <ViewSelectorDropdown selectedView={view} />
          <ActionsDropdown
            onRequeue={onOpenRebuild}
            onShuffle={onOpenShuffle}
            onEditQueue={tweetContext.onOpenEditSchedule}
          />
        </Flex>
      </Flex>
      {view === "month" ? <MonthView {...sharedProps} /> : <DayView {...sharedProps} />}

      <ConfirmPopup
        isOpen={isOpenRebuild}
        onClose={onCloseRebuild}
        title="Re-Queue"
        body={
          "Are you sure you want to re-queue your posts? This will reschedule all your posts to fill the gaps in the queue."
        }
        allowClickBackground={true}
        callback={async () => {
          await rebuildQueue(session, undefined, tweetContext.refresher);
        }}
      />
      <ConfirmPopup
        isOpen={isOpenShuffle}
        onClose={onCloseShuffle}
        title="Shuffle Queue"
        body={
          "Are you sure you want to shuffle your posts? This will shuffle all your scheduled content and push everything to the top"
        }
        allowClickBackground={true}
        callback={async () => {
          await rebuildQueue(session, undefined, tweetContext.refresher, true);
        }}
      />
    </Page>
  );
}
